/**
 * Organization members — `GET /organizations/{id}/members` and
 * `PUT /organizations/{id}/members/{userId}/roles`. Ported from
 * `bimgo-ai-web`'s `membersApi`.
 *
 * Both are **direct** shapes (a bare array, a bare object), not `{ success, data }`
 * envelopes. Editing a member's roles is an org-admin mutation — the caller
 * gates it on `org:manage_roles`, but api-v2 enforces it regardless.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";

export interface OrgMember {
  user_id: string;
  email: string | null;
  roles: string[];
  actions: string[];
}

async function requestJson(
  method: "GET" | "PUT",
  url: string,
  token: string,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      headers: {
        Authorization: bearer(token),
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const parsed: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = readDetail(parsed);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return parsed;
}

export async function getOrgMembers(
  token: string | null,
  orgId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<OrgMember[]> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");
  const base = deps.apiBaseUrl.replace(/\/+$/, "");
  const url = `${base}/organizations/${encodeURIComponent(orgId)}/members`;
  const body = await requestJson("GET", url, token, null, deps.fetchImpl ?? apiFetch);
  return Array.isArray(body) ? (body as OrgMember[]) : [];
}

export async function updateMemberRoles(
  token: string | null,
  orgId: string,
  userId: string,
  roles: string[],
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<{ user_id: string; organization_id: string; roles: string[] }> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");
  const base = deps.apiBaseUrl.replace(/\/+$/, "");
  const url = `${base}/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/roles`;
  const body = await requestJson("PUT", url, token, { roles }, deps.fetchImpl ?? apiFetch);
  return body as { user_id: string; organization_id: string; roles: string[] };
}
